import { AsyncPipe } from '@angular/common';
import { Component, DestroyRef, ElementRef, inject, viewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule, NgModel } from '@angular/forms';
import { LetDirective } from '@ngrx/component';
import { filter } from 'rxjs/operators';

import { PrivaButtonComponent } from '@priva/components/button';
import { PrivaCardComponent } from '@priva/components/card';
import { PrivaFormGroupComponent } from '@priva/components/form-group';
import { PrivaInputComponent } from '@priva/components/input';
import { PrivaLoaderComponent } from '@priva/components/loader';
import { PrivaSpinnerComponent } from '@priva/components/spinner';
import { PrivaTileComponent } from '@priva/components/tile';
import { PrivaValidationComponent, PrivaValidationItemComponent } from '@priva/components/validation';
import { PrivaLocalizationModule } from '@priva/localization';

import { ScanQrComponent } from 'app/features/scan-flow/components';
import { getScannedCodeRegex } from 'app/features/scan-flow/state/scanned-codes/utils';

import { AppWizardStep, AppWizardStepComponent } from '../../../components';
import { SiteConnectWizardStepStore } from './site-connect-wizard-step.store';

@Component({
    selector: 'app-site-connect-wizard-step',
    templateUrl: './site-connect-wizard-step.component.html',
    styleUrl: './site-connect-wizard-step.component.scss',
    imports: [
        AsyncPipe,
        FormsModule,
        LetDirective,
        PrivaButtonComponent,
        PrivaCardComponent,
        PrivaFormGroupComponent,
        PrivaInputComponent,
        PrivaLoaderComponent,
        PrivaLocalizationModule,
        PrivaSpinnerComponent,
        PrivaTileComponent,
        PrivaValidationComponent,
        PrivaValidationItemComponent,
        AppWizardStepComponent,
        ScanQrComponent,
    ],
    providers: [SiteConnectWizardStepStore],
})
export class SiteConnectWizardStepComponent extends AppWizardStep {
    private destroyRef = inject(DestroyRef);
    private componentStore = inject(SiteConnectWizardStepStore);

    scannerAvailable = true;

    manualIdentifier = '';
    isExpanded = false;

    stage$ = this.componentStore.stage$;
    error$ = this.componentStore.error$;
    identifierRegex = getScannedCodeRegex('site-identifier');

    readonly identifierInput = viewChild('identifierInput', { read: ElementRef });

    constructor() {
        super();
        super.setTitle('GENERAL.ACTION.SCAN_QR_CODE');

        this.componentStore.stage$
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                filter((stage) => stage === 'connected'),
            )
            .subscribe(() => {
                this.next$.next();
            });
    }

    protected override onShow() {
        this.resetApiConfig();
        this.scannerAvailable = true;
        this.manualIdentifier = '';
        this.canGoNext$.next(false);
    }

    resetApiConfig() {
        this.componentStore.resetApiConfig();
    }

    onCodeScanned(code: string) {
        this.componentStore.onCodeScanned(code);
    }

    onToggleExpanded() {
        this.isExpanded = !this.isExpanded;
    }

    onScannerUnavailable() {
        this.scannerAvailable = false;
    }

    submitIdentifier(identifier: NgModel) {
        if (identifier.valid) {
            this.onCodeScanned(identifier.value);
        }
    }

    blurIdentifierInput() {
        return this.identifierInput()?.nativeElement?.blur();
    }
}
